import { IRunPassage } from '@/models/Passage';
import { TrackingWorksheetColumnIds } from '@/models/TrackingWorksheet';
import {
  UtcRange,
  filterRowsByUtcRange,
  parseApiUtcTimestamp,
} from '@/utils/HistoryFilter';
import {
  getMLMean,
  getMLWaveforms,
  hasGreenML,
} from '@/utils/PassageTableRows';

/**
 * EN-1851: rows of the tracksheet run passages section (every passage on the
 * run, from GET /pigrun/{pigRunId}/passages): sort, filter, live refresh and
 * time display. Pure functions so they can be unit tested.
 */

/** Column names of the run passages table; the sortable ones are sort keys. */
export const RunPassageColumn = {
  marker: 'markerName',
  time: 'passageTimeStamp',
  uid: 'uid',
  detectionCount: 'detectionCount',
  waveforms: 'graph-data',
  verify: 'verify-data',
  ml: 'machine-learning-data',
} as const;

export type RunPassageTimeZone = 'utc' | 'local';

/**
 * EN-1851: the section and the marker popover show times as the tracksheet's
 * Pass Time column does (its UTC / Local column setting).
 */
export function tracksheetTimeZone(
  timeColumnVariants: Record<string, string> | null | undefined
): RunPassageTimeZone {
  return timeColumnVariants?.[TrackingWorksheetColumnIds.passUtc] === 'local'
    ? 'local'
    : 'utc';
}

/** The time column's header, as the tracksheet's ('Pass Time (Local)'). */
export function runPassageTimeLabel(zone: RunPassageTimeZone): string {
  return zone === 'local' ? 'Time (Local)' : 'Time (UTC)';
}

export interface RunPassageFilter {
  /** Only this marker's passages (the selected tracksheet row), or all. */
  pipelineSectionMarkerId?: number | null;
  /** UTC From / To range (an invalid range filters nothing, as the dialog). */
  range?: UtcRange;
  /** Only passages with a green ML result. */
  greenMlOnly?: boolean;
}

/** A row is one passage at one marker. */
export function runPassageRowKey(row: IRunPassage): string {
  return `${row.passageId}:${row.pipelineSectionMarkerId}`;
}

function timeMs(row: IRunPassage): number | null {
  return parseApiUtcTimestamp(row.passageTimeStamp);
}

function isVerifiedRow(row: IRunPassage): boolean {
  return (row.passageWaveforms || []).some((wf) => wf.isVerified);
}

/**
 * The best ML score of a row (-1 red .. 1 green), or null without ML results;
 * the ML column sorts by it.
 */
export function bestMlScore(row: IRunPassage): number | null {
  const scores = getMLWaveforms(row).map((wf) =>
    getMLMean(wf.mlPrediction as boolean, wf.mlConfidence as number)
  );
  return scores.length ? Math.max(...scores) : null;
}

/** The value a sort key sorts by; null sorts last in both directions. */
function sortValue(row: IRunPassage, sortBy: string): number | null {
  switch (sortBy) {
    case RunPassageColumn.time:
      return timeMs(row);
    case RunPassageColumn.marker:
      return row.markerSortOrder ?? null;
    case RunPassageColumn.uid:
      return row.uid ?? null;
    case RunPassageColumn.detectionCount:
      return row.detectionCount ?? null;
    case RunPassageColumn.verify:
      return isVerifiedRow(row) ? 1 : 0;
    case RunPassageColumn.ml:
      return bestMlScore(row);
    default:
      return null;
  }
}

/** Newest first, then marker order, then passage id (the API order). */
function compareDefault(a: IRunPassage, b: IRunPassage): number {
  const ta = timeMs(a) ?? -Infinity;
  const tb = timeMs(b) ?? -Infinity;
  if (ta !== tb) return tb - ta;
  const so = (a.markerSortOrder ?? 0) - (b.markerSortOrder ?? 0);
  if (so !== 0) return so;
  return a.passageId - b.passageId;
}

/** Ascending comparator for a sort key; ties keep the default order. */
export function compareRunPassages(
  a: IRunPassage,
  b: IRunPassage,
  sortBy: string
): number {
  return compareBy(a, b, sortBy, false);
}

function compareBy(
  a: IRunPassage,
  b: IRunPassage,
  sortBy: string,
  descending: boolean
): number {
  const va = sortValue(a, sortBy);
  const vb = sortValue(b, sortBy);
  if (va !== vb) {
    if (va === null) return 1;
    if (vb === null) return -1;
    return descending ? vb - va : va - vb;
  }
  if (sortBy === RunPassageColumn.marker) {
    const byName = (a.markerName || '').localeCompare(b.markerName || '');
    if (byName !== 0) return descending ? -byName : byName;
  }
  return compareDefault(a, b);
}

/**
 * Sorts a copy of the rows (q-table sort-method). With no sort key the rows are
 * in the default order: newest first, then marker order, then passage id.
 */
export function sortRunPassages(
  rows: readonly IRunPassage[],
  sortBy: string | null | undefined,
  descending: boolean
): IRunPassage[] {
  const copy = [...rows];
  if (!sortBy) return copy.sort(compareDefault);
  return copy.sort((a, b) => compareBy(a, b, sortBy, descending));
}

export function filterRunPassages(
  rows: readonly IRunPassage[],
  filter: RunPassageFilter
): IRunPassage[] {
  let out = [...rows];
  const markerId = filter.pipelineSectionMarkerId;
  if (markerId != null) {
    out = out.filter((r) => r.pipelineSectionMarkerId === markerId);
  }
  if (filter.range) {
    out = filterRowsByUtcRange(out, (r) => r.passageTimeStamp, filter.range);
  }
  if (filter.greenMlOnly) out = out.filter(hasGreenML);
  return out;
}

function refreshPigRunId(payload: any): number | null {
  if (typeof payload === 'number') return payload;
  if (payload && typeof payload.pigRunId === 'number') return payload.pigRunId;
  return null;
}

/** Whether a store action should refetch the run's passages. */
export function shouldRefreshRunPassages(
  action: { type: string; payload?: any },
  pigRunId: number | null,
  runUids: readonly number[]
): boolean {
  if (pigRunId == null) return false;
  const payload = action.payload;
  switch (action.type) {
    case 'passageRecieved': {
      // Without the run's devices (or a uid) we cannot tell: refresh.
      const uid = payload?.uid;
      if (!runUids.length || uid == null) return true;
      return runUids.includes(Number(uid));
    }
    case 'refreshSinglePigRun':
      return refreshPigRunId(payload) === pigRunId;
    case 'mlResultReceived':
      return (
        Array.isArray(payload?.pigRunIds) &&
        payload.pigRunIds.some((id: any) => Number(id) === pigRunId)
      );
    default:
      return false;
  }
}

/**
 * A verify / unverify of a passage (EventBus 'passage-verified'): its new
 * waveforms on every row of that passage (one per marker), as new objects.
 */
export function applyVerifiedPassage(
  rows: readonly IRunPassage[],
  passage: { passageId: number; passageWaveforms?: any[] | null }
): IRunPassage[] {
  if (!passage || !Array.isArray(passage.passageWaveforms)) return [...rows];
  return rows.map((r) =>
    r.passageId === passage.passageId
      ? {
          ...r,
          passageWaveforms: passage.passageWaveforms!.map((wf) => ({ ...wf })),
        }
      : r
  );
}

/**
 * 'YYYY-MM-DD HH:mm:ss UTC', or the browser's local time: the same as the
 * tracksheet's Pass Time (formatUtc / formatBrowserLocal).
 */
export function formatRunPassageTime(
  value: string | null | undefined,
  zone: RunPassageTimeZone
): string {
  const t = parseApiUtcTimestamp(value);
  if (t === null) return '';
  const d = new Date(t);
  if (zone === 'local') return d.toLocaleString();
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}
